import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    Grid,
    Tabs,
    Tab,
} from '@mui/material';
import axiosInstance from "../AxiosInstance";
import ForecastDropdown from "./ForecastDropdown";
import ForecastPeriodDropdown from "./ForecastPeriodDropdown";
import MonthDropdown from "./MonthDropdown";
import SalesOrdersGrid from "./SalesOrdersGrid";
import RetroTriggersGrid from "./RetroTriggersGrid";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {todayUk, getMonthEndDate} from "../Utilities/dateUtility";

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`sales-outputs-tabpanel-${index}`}
            aria-labelledby={`sales-outputs-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

const formatCurrency = (value) => {
    if (value === 0) return '£ -';
    return `£ ${value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatUnits = (value) => {
    if (value === 0) return '-';
    return value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatVarBudget = (value) => {
    if (value === 0) return '£ -';
    
    const formattedValue = `£ ${Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    
    return value < 0 ? `-${formattedValue}` : formattedValue;
};

const formatVarUnits = (value) => {
    if (value === 0) return '-';
    
    const formattedValue = Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    
    return value < 0 ? `-${formattedValue}` : formattedValue;
};

const getRowStyle = (type, isExpandable) => {
    const baseStyle = {
        ...(isExpandable && {
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
        })
    };
    
    switch (type) {
        case 'total':
            return {
                ...baseStyle,
                backgroundColor: 'rgba(var(--mui-palette-info-mainChannel) / 0.1)',
                fontWeight: 'bold',
                borderTop: '2px solid var(--mui-palette-info-main)',
                borderBottom: '2px solid var(--mui-palette-info-main)',
                '& td': {
                    fontWeight: 'bold',
                    borderBottom: 0
                }
            };
        case 'channel':
            return {
                ...baseStyle,
                bgcolor: 'rgba(var(--mui-palette-primary-mainChannel) / 0.15)',
                fontWeight: 'bold',
                '& td': {
                    borderBottom: '1px solid var(--mui-palette-primary-main)'
                }
            };
        case 'customer':
            return {
                ...baseStyle,
                bgcolor: 'rgba(var(--mui-palette-primary-mainChannel) / 0.07)',
                '& td': {
                    borderBottom: '1px solid rgba(var(--mui-palette-primary-mainChannel) / 0.5)'
                }
            };
        case 'product':
            return {
                ...baseStyle,
                backgroundColor: 'var(--mui-palette-background-paper)',
                borderBottom: '1px solid var(--mui-palette-divider)'
            };
        default:
            return baseStyle;
    }
};

function ForecastOutputsSales() {
    const [forecasts, setForecasts] = useState([]);
    const [selectedForecastId, setSelectedForecastId] = useState(null);
    const [selectedForecastPeriodId, setSelectedForecastPeriodId] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [viewMode, setViewMode] = useState('revenue'); // 'revenue' or 'units'
    const [tabValue, setTabValue] = useState(0);
    const [salesData, setSalesData] = useState({
        timeGone: 0,
        rows: []
    });
    const [loadingForecasts, setLoadingForecasts] = useState(true);
    const [loadingTable, setLoadingTable] = useState(false);
    const [expandedChannels, setExpandedChannels] = useState(new Set());
    const [expandedCustomers, setExpandedCustomers] = useState(new Set());
    const [tableHeight, setTableHeight] = useState('calc(100vh - 250px)');
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const containerRef = useRef(null);

    const getToken = () => localStorage.getItem("token");

    const setValidSelectedMonth = (monthValue) => {
        if (!monthValue || typeof monthValue !== 'string' || monthValue.includes('NaN'))
            return;
        
        const parts = monthValue.split('-');
        if (parts.length === 2) {
            const year = Number(parts[0]);
            const month = Number(parts[1]);
            if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12) {
                setSelectedMonth(monthValue);
            }
        }
    };

    useEffect(() => {
        const storedForecast = JSON.parse(localStorage.getItem('selectedForecast'));
        const storedForecastPeriodId = localStorage.getItem('selectedForecastPeriodId');

        if (storedForecast?.forecastId) {
            setSelectedForecastId(storedForecast.forecastId);
        }

        if (storedForecastPeriodId && storedForecastPeriodId !== "null") {
            setSelectedForecastPeriodId(parseInt(storedForecastPeriodId, 10));
        } else {
            setSelectedForecastPeriodId(null);
        }
        
        const today = new Date();
        const initialMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
        setValidSelectedMonth(initialMonth);

        fetchForecastsAndPeriods();
        setInitialLoadComplete(true);
    }, []);

    const fetchForecastsAndPeriods = async () => {
        try {
            setLoadingForecasts(true);
            const token = getToken();
            const response = await axiosInstance.get("/forecasts", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            
            setForecasts(response.data);
            
            const today = new Date();
            const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            const availableForecasts = response.data.filter(forecast => {
                const forecastStartDate = new Date(forecast.startDate);
                const forecastStartMonth = new Date(forecastStartDate.getFullYear(), forecastStartDate.getMonth(), 1);
                return forecastStartMonth <= currentMonth;
            });
            
            const storedForecast = JSON.parse(localStorage.getItem('selectedForecast'));
            
            if (storedForecast?.forecastId && availableForecasts.length > 0) {
                const forecast = availableForecasts.find(f => f.forecastId === storedForecast.forecastId);
                
                if (forecast) {
                    const forecastStartDate = new Date(forecast.startDate);
                    const startMonth = new Date(forecastStartDate.getFullYear(), forecastStartDate.getMonth(), 1);
                    const today = new Date();
                    const twelveMonthsAfterStart = new Date(startMonth.getFullYear(), startMonth.getMonth() + 12, 0);
                    
                    const isCurrentMonthInRange = today >= startMonth && today <= twelveMonthsAfterStart;
                    
                    if (isCurrentMonthInRange) {
                        const currentMonthValue = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
                        setValidSelectedMonth(currentMonthValue);
                    } else {
                        const firstMonth = `${startMonth.getFullYear()}-${String(startMonth.getMonth() + 1).padStart(2, '0')}`;
                        setValidSelectedMonth(firstMonth);
                    }
                } else {
                    localStorage.removeItem('selectedForecast');
                    localStorage.removeItem('selectedForecastPeriodId');
                    setSelectedForecastId(null);
                    setSelectedForecastPeriodId(null);
                }
            } else if (availableForecasts.length === 0) {
                localStorage.removeItem('selectedForecast');
                localStorage.removeItem('selectedForecastPeriodId');
                setSelectedForecastId(null);
                setSelectedForecastPeriodId(null);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingForecasts(false);
        }
    };

    useEffect(() => {
        if (!initialLoadComplete) {
            return;
        }

        const fetchSalesData = async () => {
            setLoadingTable(true);
            try {
                const endDate = selectedMonth ? getMonthEndDate(selectedMonth) : new Date();
                
                const params = { 
                    forecastPeriodId: selectedForecastPeriodId || 0,
                    endDate: endDate.toISOString()
                };
                const forecastIdToUse = selectedForecastId || 0;
                const response = await axiosInstance.get(`/forecasts/${forecastIdToUse}/outputs-sales`, { params });
                setSalesData(response.data);
            } catch (error) {
                console.error("Failed to fetch sales outputs:", error);
                setSalesData({
                    timeGone: 0,
                    rows: []
                });
            } finally {
                setLoadingTable(false);
            }
        };

        fetchSalesData();
    }, [selectedForecastId, selectedForecastPeriodId, selectedMonth, initialLoadComplete]);

    const availableForecasts = useMemo(() => {
        const today = new Date();
        const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        
        return forecasts.filter(forecast => {
            const forecastStartDate = new Date(forecast.startDate);
            const forecastStartMonth = new Date(forecastStartDate.getFullYear(), forecastStartDate.getMonth(), 1);
            return forecastStartMonth <= currentMonth;
        });
    }, [forecasts]);

    const selectedForecast = availableForecasts.find((f) => f.forecastId === selectedForecastId);

    const handleForecastSelected = (id) => {
        localStorage.setItem("selectedForecast", JSON.stringify({forecastId: id}));
        setSelectedForecastId(id);
        setSelectedForecastPeriodId(null);
        
        const newSelectedForecast = forecasts.find(f => f.forecastId === id);
        if (newSelectedForecast) {
            const forecastStartDate = new Date(newSelectedForecast.startDate);
            const startMonth = new Date(forecastStartDate.getFullYear(), forecastStartDate.getMonth(), 1);
            const today = new Date();
            const twelveMonthsAfterStart = new Date(startMonth.getFullYear(), startMonth.getMonth() + 12, 0);
            
            const isCurrentMonthInRange = today >= startMonth && today <= twelveMonthsAfterStart;
            
            if (isCurrentMonthInRange) {
                const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
                setValidSelectedMonth(currentMonth);
            } else {
                const firstMonth = `${startMonth.getFullYear()}-${String(startMonth.getMonth() + 1).padStart(2, '0')}`;
                setValidSelectedMonth(firstMonth);
            }
        }
    };

    const handleForecastPeriodSelected = (forecastPeriodId) => {
        localStorage.setItem("selectedForecastPeriodId", forecastPeriodId);
        setSelectedForecastPeriodId(forecastPeriodId);
    };
    
    const handleMonthSelected = (month) => {
        setValidSelectedMonth(month);
    };

    const handleViewModeChange = (event, newViewMode) => {
        if (newViewMode !== null) {
            setViewMode(newViewMode);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const toggleChannel = (channelName, event) => {
        if (event && event.target.closest('button')) return;
        
        setExpandedChannels(prev => {
            const newSet = new Set(prev);
            if (newSet.has(channelName)) {
                newSet.delete(channelName);
                setExpandedCustomers(prevCustomers => {
                    const newCustomerSet = new Set(prevCustomers);
                    salesData.rows.forEach((row, index) => {
                        if (row.type === 'customer') {
                            for (let i = index - 1; i >= 0; i--) {
                                if (salesData.rows[i].type === 'channel' && 
                                    salesData.rows[i].description === channelName) {
                                    newCustomerSet.delete(row.description);
                                    break;
                                }
                            }
                        }
                    });
                    return newCustomerSet;
                });
            } else {
                newSet.add(channelName);
            }
            return newSet;
        });
    };

    const toggleCustomer = (customerName, event) => {
        if (event && event.target.closest('button')) return;
        
        setExpandedCustomers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(customerName)) {
                newSet.delete(customerName);
            } else {
                newSet.add(customerName);
            }
            return newSet;
        });
    };

    const isRowVisible = (row, index, rows) => {
        if (row.type === 'total') return true;
        
        if (row.type === 'channel') return true;
        
        if (row.type === 'customer') {
            for (let i = index - 1; i >= 0; i--) {
                if (rows[i].type === 'channel') {
                    return expandedChannels.has(rows[i].description);
                }
            }
            return false;
        }
        
        if (row.type === 'product') {
            let parentCustomer = null;
            let parentChannel = null;
            
            for (let i = index - 1; i >= 0; i--) {
                if (!parentCustomer && rows[i].type === 'customer') {
                    parentCustomer = rows[i].description;
                }
                if (!parentChannel && rows[i].type === 'channel') {
                    parentChannel = rows[i].description;
                    break;
                }
            }
            
            return parentCustomer && parentChannel && 
                   expandedChannels.has(parentChannel) && 
                   expandedCustomers.has(parentCustomer);
        }
        
        return true;
    };

    useEffect(() => {
        const tableContainerRef = containerRef.current?.querySelector('.MuiTableContainer-root');
        
        const updateHeight = () => {
            if (containerRef.current && tableContainerRef) {
                const viewportHeight = window.innerHeight;
                const tableTop = tableContainerRef.getBoundingClientRect().top;
                setTableHeight(`${viewportHeight - tableTop - 20}px`);
            }
        };
        
        updateHeight();
        
        window.addEventListener('resize', updateHeight);
        
        return () => {
            window.removeEventListener('resize', updateHeight);
        };
    }, [loadingTable, salesData.rows]);

    const getValue = (row, field) => {
        if (viewMode === 'units') {
            switch (field) {
                case 'budget': return row.budgetUnits;
                case 'actual': return row.actualUnits;
                case 'leLock': return row.leLockUnits;
                case 'le': return row.leUnits;
                case 'mtg': return row.mtgUnits;
                case 'varBudget': return row.varBudgetUnits;
                case 'varActualsBudget': return row.varActualsBudgetUnits;
                case 'percentVsLe': return row.percentVsLeUnits;
                default: return row[field];
            }
        } else {
            return row[field];
        }
    };

    const formatValue = (row, field) => {
        const value = getValue(row, field);
        
        if (viewMode === 'units') {
            if (field === 'varBudget' || field === 'varActualsBudget') {
                return formatVarUnits(value);
            }
            return formatUnits(value);
        } else {
            if (field === 'varBudget' || field === 'varActualsBudget') {
                return formatVarBudget(value);
            }
            return formatCurrency(value);
        }
    };

    const getDisplayDate = () => {
        if (!selectedMonth) {
            return todayUk();
        }
        
        const parts = selectedMonth.split('-');
        if (parts.length !== 2) {
            return todayUk();
        }
        
        const selectedYear = Number(parts[0]);
        const selectedMonthNum = Number(parts[1]);
        
        if (isNaN(selectedYear) || isNaN(selectedMonthNum)) {
            return todayUk();
        }
        
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1; // getMonth() is 0-based
        
        if (selectedYear === currentYear && selectedMonthNum === currentMonth) {
            return todayUk();
        }
        
        const lastDayOfMonth = new Date(selectedYear, selectedMonthNum, 0).getDate(); // day 0 = last day of previous month
        const lastDateOfMonth = new Date(selectedYear, selectedMonthNum - 1, lastDayOfMonth);
        
        const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
        return lastDateOfMonth.toLocaleDateString('en-GB', options);
    };

    return (
        <Box className="component-container">
            <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="sales outputs tabs">
                    <Tab label="Sales Actuals" id="sales-outputs-tab-0" aria-controls="sales-outputs-tabpanel-0" />
                    <Tab label="Sales Orders" id="sales-outputs-tab-1" aria-controls="sales-outputs-tabpanel-1" />
                    <Tab label="Retro Triggers" id="sales-outputs-tab-2" aria-controls="sales-outputs-tabpanel-2" />
                </Tabs>
            </Paper>

            <TabPanel value={tabValue} index={0}>
                <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} sm="auto">
                            {loadingForecasts ? (
                                <Skeleton variant="rectangular" width={200} height={56} />
                            ) : (
                                <ForecastDropdown
                                    forecasts={availableForecasts}
                                    selectedForecastId={selectedForecastId}
                                    onForecastSelected={handleForecastSelected}
                                    width={200}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} sm="auto">
                            {loadingForecasts ? (
                                <Skeleton variant="rectangular" width={200} height={56} />
                            ) : (
                                <ForecastPeriodDropdown
                                    forecastPeriods={selectedForecast?.forecastPeriods || []}
                                    selectedForecastPeriodId={selectedForecastPeriodId}
                                    onForecastPeriodSelected={handleForecastPeriodSelected}
                                    width={200}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} sm="auto">
                            {loadingForecasts ? (
                                <Skeleton variant="rectangular" width={150} height={56} />
                            ) : (
                                <MonthDropdown
                                    selectedForecast={selectedForecast}
                                    selectedMonth={selectedMonth}
                                    onMonthSelected={handleMonthSelected}
                                    disabled={false}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} sm="auto">
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={handleViewModeChange}
                                aria-label="view mode"
                                size="small"
                            >
                                <ToggleButton value="revenue" aria-label="revenue view">
                                    Revenue
                                </ToggleButton>
                                <ToggleButton value="units" aria-label="units view">
                                    Units
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>
                    </Grid>
                </Paper>
            
                {loadingTable ? (
                    <Skeleton variant="rectangular" width="100%" height={600} />
                ) : (
                    salesData.rows && salesData.rows.length > 0 && (
                        <Box sx={{ p: 3 }} ref={containerRef}>
                            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle1">
                                    {viewMode === 'units' ? 'Units' : 'Gross Rev'} {getDisplayDate()}
                                </Typography>
                                <Typography variant="subtitle1">
                                    Time Gone {salesData.timeGone}%
                                </Typography>
                            </Box>

                            <TableContainer component={Paper} sx={{ maxHeight: tableHeight }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow sx={{ fontWeight: 'bold' }}>
                                            <TableCell sx={{ backgroundColor: 'background.paper' }}></TableCell>
                                            <TableCell sx={{ backgroundColor: 'background.paper' }} align="right">Budget</TableCell>
                                            <TableCell sx={{ backgroundColor: 'background.paper' }} align="right">Actuals</TableCell>
                                            <TableCell sx={{ backgroundColor: 'background.paper' }} align="right">LE Lock</TableCell>
                                            <TableCell sx={{ backgroundColor: 'background.paper' }} align="right">LE</TableCell>
                                            <TableCell sx={{ backgroundColor: 'background.paper' }} align="right">MTG</TableCell>
                                            <TableCell sx={{ backgroundColor: 'background.paper' }} align="right">Var LE vs Budget</TableCell>
                                            <TableCell sx={{ backgroundColor: 'background.paper' }} align="right">Var Actuals vs Budget</TableCell>
                                            <TableCell sx={{ backgroundColor: 'background.paper' }} align="right">% vs LE</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {salesData.rows.map((row, index) => 
                                            isRowVisible(row, index, salesData.rows) && (
                                                <TableRow 
                                                    key={index}
                                                    sx={getRowStyle(
                                                        row.type, 
                                                        row.type === 'channel' || row.type === 'customer'
                                                    )}
                                                    onClick={(e) => {
                                                        if (row.type === 'channel') {
                                                            toggleChannel(row.description, e);
                                                        } else if (row.type === 'customer') {
                                                            toggleCustomer(row.description, e);
                                                        }
                                                    }}
                                                >
                                                    <TableCell 
                                                        sx={{ 
                                                            paddingLeft: row.type === 'customer' ? '1.25rem' : row.type === 'product' ? '1.75rem' : '0.75rem',
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        {row.type === 'channel' && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleChannel(row.description);
                                                                }}
                                                                sx={{ mr: 1, p: 0 }}
                                                            >
                                                                {expandedChannels.has(row.description) ? 
                                                                    <KeyboardArrowDownIcon /> : 
                                                                    <KeyboardArrowRightIcon />}
                                                            </IconButton>
                                                        )}
                                                        {row.type === 'customer' && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleCustomer(row.description);
                                                                }}
                                                                sx={{ mr: 1, p: 0 }}
                                                            >
                                                                {expandedCustomers.has(row.description) ? 
                                                                    <KeyboardArrowDownIcon /> : 
                                                                    <KeyboardArrowRightIcon />}
                                                            </IconButton>
                                                        )}
                                                        {row.description}
                                                    </TableCell>
                                                    <TableCell align="right">{formatValue(row, 'budget')}</TableCell>
                                                    <TableCell align="right">{formatValue(row, 'actual')}</TableCell>
                                                    <TableCell align="right">{formatValue(row, 'leLock')}</TableCell>
                                                    <TableCell align="right">{formatValue(row, 'le')}</TableCell>
                                                    <TableCell align="right">{formatValue(row, 'mtg')}</TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            color: getValue(row, 'varBudget') > 0 
                                                                ? 'var(--mui-palette-success-main)' 
                                                                : getValue(row, 'varBudget') < 0 
                                                                    ? 'var(--mui-palette-error-main)' 
                                                                    : 'inherit'
                                                        }}
                                                    >
                                                        {formatValue(row, 'varBudget')}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            color: getValue(row, 'varActualsBudget') > 0 
                                                                ? 'var(--mui-palette-success-main)' 
                                                                : getValue(row, 'varActualsBudget') < 0 
                                                                    ? 'var(--mui-palette-error-main)' 
                                                                    : 'inherit'
                                                        }}
                                                    >
                                                        {formatValue(row, 'varActualsBudget')}
                                                    </TableCell>
                                                    <TableCell align="right">{getValue(row, 'percentVsLe') ? `${getValue(row, 'percentVsLe')}%` : ''}</TableCell>
                                                </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )
                )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                <Box sx={{ height: 'calc(100vh - 250px)' }}>
                    <SalesOrdersGrid selectedMonth={selectedMonth} />
                </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
                <Box sx={{ height: 'calc(100vh - 250px)' }}>
                    <RetroTriggersGrid />
                </Box>
            </TabPanel>
        </Box>
    );
}

export default ForecastOutputsSales; 